import { SharedLocationStore } from './shared-location/shared-location-store';
import ScheduleStore from './schedule/schedule-store';
import DriverTrackingStore from './driver-tracking/driver-tracking-store';
import CustomerTrackingStore from './customer-tracking/customer-tracking-store';
import { getEnv } from 'mobx-easy';
import { observable, action, makeObservable } from 'mobx';
import { CustomerExperienceStore } from './customer-experience/customer-experience-store';
import { ApiVersion } from '@bringg/types';
import { RootEnv } from '../helpers/create-store';
import AriaStore from './aria/aria-store';
import RescheduleStore from './reschedule/reschedule-store';

class RootStore {
	sharedLocationStore: SharedLocationStore;
	customerExperienceStore: CustomerExperienceStore;
	scheduleStore: ScheduleStore;
	driverTrackingStore: DriverTrackingStore;
	customerTrackingStore: CustomerTrackingStore;
	ariaStore: AriaStore;
	rescheduleStore: RescheduleStore;

	apiVersion: ApiVersion = null;

	isError: boolean = false;

	constructor() {
		makeObservable(this, {
			apiVersion: observable,
			isError: observable,
			setApiVersion: action,
			setIsError: action
		});
	}

	setApiVersion(apiVersion: ApiVersion) {
		this.apiVersion = apiVersion;
	}

	setIsError(isError: boolean) {
		this.isError = isError;
	}

	init() {
		this.sharedLocationStore = new SharedLocationStore();
		this.customerExperienceStore = new CustomerExperienceStore();
		this.scheduleStore = new ScheduleStore();
		this.driverTrackingStore = new DriverTrackingStore();
		this.customerTrackingStore = new CustomerTrackingStore();
		this.ariaStore = new AriaStore();
		this.rescheduleStore = new RescheduleStore();
	}

	private get configStore(): SharedLocationStore | CustomerExperienceStore {
		switch (this.apiVersion) {
			case ApiVersion.V1:
				return this.sharedLocationStore;
			case ApiVersion.V2:
				return this.customerExperienceStore;
			default:
				return null;
		}
	}

	initCustomerExperience = async (sharedLocationUuid: string) => {
		const { customerExperienceService } = getEnv<RootEnv>();
		const apiVersion = (await customerExperienceService.getApiVersion(sharedLocationUuid))?.api_version;

		this.setApiVersion(apiVersion);

		if (!this.configStore) {
			throw new Error('Api version not supported');
		}

		return this.configStore.initialize(sharedLocationUuid);
	};

	// Re-reads the order once it has changed under us (a reschedule), so the screens behind the
	// change stop rendering the payload the page loaded with. Config only — the socket, the language
	// bundle and the sharing mode are already set up by initCustomerExperience and are left alone.
	refreshCustomerExperience = async () => {
		if (!this.configStore) {
			return;
		}

		await this.configStore.refresh();

		// the window picked earlier in this session would otherwise shadow the one just re-read
		this.scheduleStore.setDeliveryWindow(null);
	};
}

export default RootStore;
